import { DownloadOutlined } from '@ant-design/icons';
import { type ApolloQueryResult } from '@apollo/client';
import { Button, Form, Grid, Tabs } from 'antd';
import { useFormikContext } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type FinderVehicleSpecsFragment } from '../../../api/fragments/FinderVehicleSpecs';
import { type GetVehicleQuery, type GetVehicleQueryVariables } from '../../../api/queries/getVehicle';
import { ApplicationStage } from '../../../api/types';
import { useAccountContext } from '../../../components/contexts/AccountContextManager';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import { useMultipleDealerIds } from '../../../components/contexts/DealerContextManager';
import {
    type ExportModalInput,
    useExportFormatModal,
} from '../../../components/fields/DownloadModal/ExportFormatModal';
import { usePasswordModal } from '../../../components/fields/DownloadModal/PasswordModal';
import FormAutoTouch from '../../../components/FormAutoTouch';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import { useThemeComponents } from '../../../themes';
import { ExportFormat, exportFinderApplications } from '../../../utilities/export';
import { useAddLtaFormDrawer } from './AddLtaDrawer';
import FinderVehicleDetails from './FinderVehicleDetails';
import { type FinderVehicleFormValues } from './shared';

type InnerProps = {
    isPartnerNumberSetup: boolean;
    refetch: (variables?: Partial<GetVehicleQueryVariables>) => Promise<ApolloQueryResult<GetVehicleQuery>>;
    hasLTA: boolean;
    finderVehicleVin: string;
    disabled?: boolean;
};

export enum HeaderTabs {
    VehicleDetails = 'vehicleDetails',
    Financing = 'financing',
    Appointment = 'appointment',
    Lead = 'lead',
    Reservations = 'reservations',
    Insurance = 'insurance',
}

const Inner = ({ isPartnerNumberSetup, refetch, hasLTA, finderVehicleVin, disabled = false }: InnerProps) => {
    const { t } = useTranslation(['finderVehicle']);
    const navigate = useNavigate();
    const { notification } = useThemeComponents();
    const screens = Grid.useBreakpoint();
    const { dealerIds } = useMultipleDealerIds();
    const { token } = useAccountContext();
    const company = useCompany(true);
    const passwordModal = usePasswordModal();

    const { handleSubmit, setFieldValue, values } = useFormikContext<FinderVehicleFormValues>();

    const setLta = useCallback(
        async (lta: Partial<FinderVehicleSpecsFragment['lta']>) => {
            await setFieldValue('lta', lta);
            setFieldValue('isLTAUploaded', true);
        },
        [setFieldValue]
    );

    const { open, render } = useAddLtaFormDrawer(setLta);
    const [activeTab, setActiveTab] = useState<HeaderTabs>(HeaderTabs.VehicleDetails);
    const [selectedApplications, setSelectedApplications] = useState<string[]>([]);

    const footer = useMemo(
        () =>
            !disabled &&
            [
                <Button key="finderVehicleUpdate" form="finderVehicleUpdate" htmlType="submit" type="primary">
                    {t('finderVehicle:actions.update')}
                </Button>,
            ].filter(Boolean),
        [disabled, t]
    );

    const onChangeTab = useCallback((activeKey: HeaderTabs) => {
        setActiveTab(activeKey);
        // reset the selected applications when switching tabs
        setSelectedApplications([]);
    }, []);

    const exportFormatModal = useExportFormatModal({ format: ExportFormat.DefaultFormat });

    const downloadHandler = useCallback(
        async ({ format }: ExportModalInput) => {
            notification.loading({
                content: t(`modelConfiguratorDetails:messages.loadingExportConfiguratorLeads`),
                key: 'loading',
            });

            const getExportInfoByTab = (tab: HeaderTabs) => {
                switch (tab) {
                    case HeaderTabs.Financing:
                        return { stage: ApplicationStage.Financing, applicationIds: selectedApplications };

                    case HeaderTabs.Reservations:
                        return { stage: ApplicationStage.Reservation, applicationIds: selectedApplications };

                    case HeaderTabs.Lead:
                        return { stage: ApplicationStage.Lead, applicationIds: selectedApplications };

                    case HeaderTabs.Appointment:
                        return { stage: ApplicationStage.Appointment, applicationIds: selectedApplications };

                    case HeaderTabs.Insurance:
                        return { stage: ApplicationStage.Insurance, applicationIds: selectedApplications };

                    default:
                        throw new Error('Invalid tab for downloading applications');
                }
            };

            const { stage, applicationIds } = getExportInfoByTab(activeTab);

            const password = await exportFinderApplications({
                applicationIds,
                stage,
                dealerIds,
                company: { countryCode: company?.countryCode, displayName: company?.displayName },
                token,
                format,
            });

            notification.destroy('loading');
            exportFormatModal.close();
            if (password) {
                passwordModal.open(password);
            }
        },
        [
            notification,
            t,
            activeTab,
            dealerIds,
            company?.countryCode,
            company?.displayName,
            token,
            exportFormatModal,
            selectedApplications,
            passwordModal,
        ]
    );
    const onDownloadApplicationClick = useCallback(() => {
        exportFormatModal.open();
    }, [exportFormatModal]);

    const extra = useMemo(
        () =>
            activeTab !== HeaderTabs.VehicleDetails && (
                <Button
                    disabled={!selectedApplications.length}
                    icon={<DownloadOutlined />}
                    onClick={onDownloadApplicationClick}
                    type="primary"
                >
                    {t('finderVehicle:actions.download')}
                </Button>
            ),
        [activeTab, onDownloadApplicationClick, selectedApplications.length, t]
    );

    return (
        <>
            {passwordModal.render()}

            <ConsolePageWithHeader
                extra={extra}
                footer={footer}
                header={{
                    footer: (
                        <Tabs activeKey={activeTab} onChange={onChangeTab}>
                            <Tabs.TabPane key={HeaderTabs.VehicleDetails} tab={t('finderVehicle:tabs.details')} />
                            <Tabs.TabPane key={HeaderTabs.Financing} tab={t('finderVehicle:tabs.financing')} />
                            <Tabs.TabPane key={HeaderTabs.Insurance} tab={t('finderVehicle:tabs.insurance')} />
                            <Tabs.TabPane key={HeaderTabs.Lead} tab={t('finderVehicle:tabs.lead')} />
                            <Tabs.TabPane key={HeaderTabs.Appointment} tab={t('finderVehicle:tabs.appointment')} />
                            <Tabs.TabPane key={HeaderTabs.Reservations} tab={t('finderVehicle:tabs.reservation')} />
                        </Tabs>
                    ),
                }}
                onBack={() => navigate('/admin/finderVehicles')}
                title={
                    screens.md
                        ? t('finderVehicle:title', {
                              name: values.listing.vehicle.name.localize,
                              id: values.identifier,
                          })
                        : `${values.listing.vehicle.name.localize} ${values.identifier}`
                }
            >
                <Form
                    id="finderVehicleUpdate"
                    layout="vertical"
                    name="finderVehicleUpdate"
                    onSubmitCapture={handleSubmit}
                >
                    <FormAutoTouch />
                    <FinderVehicleDetails
                        activeTab={activeTab}
                        disabled={disabled}
                        finderVehicleVin={finderVehicleVin}
                        hasLTA={hasLTA}
                        isPartnerNumberSetup={isPartnerNumberSetup}
                        openLtaDrawer={open}
                        refetch={refetch}
                        selectedApplications={selectedApplications}
                        setSelectedApplications={setSelectedApplications}
                    />
                </Form>
            </ConsolePageWithHeader>
            {render()}

            {exportFormatModal.render({
                title: t('modelConfiguratorDetails:modals.downloadApplication.title'),
                description: t('modelConfiguratorDetails:modals.downloadApplication.description'),
                onDownload: downloadHandler,
                forAdmin: true,
            })}
        </>
    );
};

export default Inner;
